// Web Speech API — free, no backend, available in all modern browsers.
// Provides "read selection" + "read page" capabilities for tier A.

let current: SpeechSynthesisUtterance | null = null;

/**
 * Cache of the last meaningful text selection on the page.
 *
 * Why we need this: clicking the FAB (or any panel button) shifts focus away
 * from the document, which clears `window.getSelection()` synchronously. By
 * the time the user reaches "read selection" inside the panel, the live
 * selection is gone. We continuously snapshot the selection while the user
 * is highlighting text, so when they click the button we have it.
 */
let lastSelectionText = '';
let selectionTracking = false;

export function ttsAvailable(): boolean {
  return typeof window !== 'undefined' && 'speechSynthesis' in window;
}

/** Start listening for selection changes. Idempotent. Call once on bootstrap. */
export function startSelectionTracking(): void {
  if (selectionTracking || typeof document === 'undefined') return;
  selectionTracking = true;
  document.addEventListener(
    'selectionchange',
    () => {
      const text = window.getSelection()?.toString().trim() ?? '';
      if (text.length >= 2) lastSelectionText = text;
    },
    { passive: true },
  );
}

/** Returns the current selection if any, otherwise the cached snapshot. */
export function getEffectiveSelection(): string {
  const live = window.getSelection()?.toString().trim() ?? '';
  return live || lastSelectionText;
}

export function speak(text: string, lang = 'he-IL'): void {
  if (!ttsAvailable()) return;
  stop();
  const u = new SpeechSynthesisUtterance(text);
  u.lang = lang;
  u.rate = 1.0;
  u.pitch = 1.0;
  current = u;
  window.speechSynthesis.speak(u);
}

export function stop(): void {
  if (!ttsAvailable()) return;
  window.speechSynthesis.cancel();
  current = null;
}

export function pause(): void {
  if (!ttsAvailable()) return;
  window.speechSynthesis.pause();
}

export function resume(): void {
  if (!ttsAvailable()) return;
  window.speechSynthesis.resume();
}

/**
 * Read the active text selection. Returns false if no selection (live or cached)
 * is available — caller should show a hint like "select text first".
 */
export function readSelection(lang?: string): boolean {
  const sel = getEffectiveSelection();
  if (!sel) return false;
  speak(sel, lang || guessLangFromDoc());
  return true;
}

export function readPage(lang?: string): void {
  const main =
    document.querySelector<HTMLElement>('main') ||
    document.querySelector<HTMLElement>('[role=main]') ||
    document.body;
  const text = (main.innerText || '').slice(0, 5000);
  speak(text, lang || guessLangFromDoc());
}

/**
 * Best BCP-47 tag for the current page so the browser picks a matching voice.
 * Global-first: honor the page's real language (any language), and only fall
 * back to Hebrew when the page declares no language at all. Previously this
 * hard-defaulted to he-IL, so a French/Japanese/Chinese page was read aloud by
 * a Hebrew voice — broken for every non-mapped language.
 */
function guessLangFromDoc(): string {
  // Full tag first (e.g. "pt-BR", "zh-Hant") — respect region/script if present.
  const full = document.documentElement.getAttribute('lang')?.trim();
  if (full && /^[a-z]{2,3}(-[A-Za-z0-9]{2,8})*$/i.test(full)) return full;
  // A few common 2-letter → region defaults for better voice selection.
  const l = full?.slice(0, 2).toLowerCase();
  const REGION: Record<string, string> = {
    he: 'he-IL', ar: 'ar-SA', ru: 'ru-RU', en: 'en-US', fr: 'fr-FR',
    es: 'es-ES', de: 'de-DE', it: 'it-IT', pt: 'pt-PT', ja: 'ja-JP',
    zh: 'zh-CN', ko: 'ko-KR', hi: 'hi-IN', nl: 'nl-NL', pl: 'pl-PL',
    tr: 'tr-TR', uk: 'uk-UA',
  };
  if (l && REGION[l]) return REGION[l];
  if (l) return l; // unknown 2-letter code → let the browser resolve it
  return 'he-IL'; // page declares no language → Hebrew (our home default)
}
